import { ChangeEvent, FC, useRef, useEffect, Fragment, useMemo } from 'react'
import { StyleFormWrapper, StyleLabel } from '../style'
import {
  StyleIconWrapper,
  StyleInput,
  StyleInputWrapper,
  StyleNotData,
} from './style'
import { Icons } from '@assets'
import { Field } from 'formik'
import Svg from '../../svg'
import ErrorMessage from '../ErrorMessage'
import { useState } from 'react'
import {
  StyleDropDownOptionWrapper,
  StyleDropDownOption,
  StyleDropDownOptionItems,
} from './style'
import debounce from '@utils/debounce'
interface InputSearchProps {
  name: string
  label?: string
  placeholder?: string
  onSearch?: (value: string) => void
  onFocus?: () => void
  onBlur?: () => void
  required?: boolean
  styleInput?: string
  styleLabel?: string
  styleWrapper?: string
  icon?: keyof typeof Icons
  side?: 'left' | 'right'
  readOnly?: boolean
  disabled?: boolean
  valueAsKey?: string
  titleAsKey?: string
  isServerside?: boolean
  options?: Array<any>
}
const InputSearch: FC<InputSearchProps> = ({
  label,
  name,
  onSearch,
  styleWrapper = '',
  styleLabel = '',
  icon,
  side = 'left',
  options = [],
  titleAsKey,
  valueAsKey,
  disabled,
  isServerside,
  onFocus,
  onBlur,
  ...restProps
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [isFocus, setIsFocus] = useState<boolean>(false)
  const [search, setSearch] = useState<string>('')
  const dropdownRef = useRef<HTMLDivElement>(null)

  const handleSearch = useMemo(() => {
    return debounce((value: string) => {
      onSearch?.(value)
    }, 600)
  }, [onSearch])

  const onClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false)
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', onClickOutside)
    return () => {
      document.removeEventListener('mousedown', onClickOutside)
    }
  }, [])
  return (
    <Field name={name}>
      {({ field, meta, form }) => {
        const hasError = meta.touched && meta.error
        return (
          <>
            <StyleFormWrapper
              styleWrapper={styleWrapper}
              onClick={() => {
                !disabled && setIsOpen(true)
              }}
            >
              {!!label && (
                <StyleLabel hasError={hasError} styleLabel={styleLabel}>
                  {label}
                </StyleLabel>
              )}
              <StyleInputWrapper>
                {!!icon && (
                  <StyleIconWrapper side={side}>
                    <Svg svg={icon} size={20} color="iconPrimary" />
                  </StyleIconWrapper>
                )}
                <StyleInput
                  isFocus={isFocus}
                  type={'text'}
                  value={field?.value || ''}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    const value = event.target.value
                    handleSearch(value)
                    form.setFieldValue(name, value)
                    if (!isServerside) {
                      setSearch(value)
                    }
                  }}
                  hasError={hasError}
                  side={side}
                  hasIcon={!!icon}
                  onFocus={() => {
                    setIsFocus(true)
                    !!onFocus && onFocus()
                  }}
                  onBlur={() => {
                    setIsFocus(false)
                    !!onBlur && onBlur()
                  }}
                  {...restProps}
                />
              </StyleInputWrapper>

              {!!hasError && <ErrorMessage message={meta.error} />}
            </StyleFormWrapper>
            {!!isOpen && (
              <StyleDropDownOptionWrapper
                ref={dropdownRef}
                top={!!label ? 80 : 50}
              >
                <StyleDropDownOptionItems>
                  {!!options?.length ? (
                    <>
                      {(!!isServerside
                        ? options
                        : options?.filter((option: any) =>
                            option[titleAsKey]
                              ?.toLowerCase()
                              ?.includes(search.toLowerCase()),
                          )
                      )?.map((option) => (
                        <Fragment key={option[valueAsKey]}>
                          <StyleDropDownOption
                            isActive={field.value === option[valueAsKey]}
                            onClick={() => {
                              form.setFieldValue(name, option[valueAsKey])
                              setIsOpen(false)
                            }}
                          >
                            {option[titleAsKey]}
                          </StyleDropDownOption>
                        </Fragment>
                      ))}
                    </>
                  ) : (
                    <StyleNotData>No Data Found</StyleNotData>
                  )}
                </StyleDropDownOptionItems>
              </StyleDropDownOptionWrapper>
            )}
          </>
        )
      }}
    </Field>
  )
}

export default InputSearch
